import * as React from "react";
import { ColorPaletteProp } from "@mui/joy/styles";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Checkbox from "@mui/joy/Checkbox";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BlockIcon from "@mui/icons-material/Block";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";

import { listWorkOrders } from "../api/methods";
import { WorkOrder } from "../api/interfaces";
import AlertDialogModal from "./ConfirmationModal";
import { RowMenuProps } from "../api/interfaces";
import { formatDate } from "../utils";
import WorkOrderDetailsModal from "./WorkOrderDetailsModal";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function OrderTable() {
  const [order, setOrder] = React.useState<Order>("desc");
  const [selectedWorkOrder, setSelectedWorkOrder] =
    React.useState<WorkOrder | null>(null);
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [equipmentCodeFilter, setEquipmentCodeFilter] = React.useState("");
  const [agentFilter, setAgentFilter] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rows, setRows] = React.useState<WorkOrder[]>([]);
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(
    null
  );

  const handleView = (id: string) => {
    const workOrder = rows.find((row) => row._id === id);
    setSelectedWorkOrder(workOrder || null);
    setOpen(true);
  };

  const handleViewClose = () => {
    setOpen(false);
    setSelectedWorkOrder(null);
  };

  const handleOpen = (itemId: string) => {
    setSelectedItemId(itemId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const RowMenu: React.FC<RowMenuProps> = ({ id }) => {
    return (
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <MenuItem onClick={() => handleView(id)}>Voir</MenuItem>
          <Divider />
          <MenuItem color="danger" onClick={() => handleOpen(id)}>
            Supprimer
          </MenuItem>
        </Menu>
      </Dropdown>
    );
  };

  const handleDeleteSuccess = () => {
    if (selectedItemId) {
      setRows((prevRows) =>
        prevRows.filter((row) => row._id !== selectedItemId)
      );
      setOpen(false);
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const fetchWorkOrders = async () => {
      try {
        const response = await listWorkOrders();
        setRows(response.data);
        console.log(response.data);
      } catch (error) {
        console.log("Failed to fetch work orders");
      } finally {
        console.log("OK");
        // setLoading(false);
      }
    };

    fetchWorkOrders();

    return () => {
      // Clean up on unmount
      isMounted = false;
    };
  }, []);

  const rowsPerPage = 5;
  const uniqueStatuses = Array.from(new Set(rows.map((row) => row?.status_ot)));
  const uniqueAgents = Array.from(new Set(rows.map((row) => row?.agent_name)));
  const indexOfLastRow = (currentPage + 1) * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const uniqueCode = Array.from(
    new Set(
      rows
        .map((row) => row.equipment_code)
        .filter((code) => code !== null && code !== undefined)
    )
  );

  const handleStatusFilterChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    console.log(newValue);
    setStatusFilter(newValue !== null ? newValue : "");
  };

  const handleAgentFilterChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    console.log(newValue);
    setAgentFilter(newValue !== null ? newValue : "");
  };

  const handleEquipmentCodeFilterChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    setEquipmentCodeFilter(newValue !== null ? newValue : "");
  };

  const filteredRows = rows.filter((row) => {
    return (
      (!equipmentCodeFilter ||
        row.equipment_code?.includes(equipmentCodeFilter)) &&
      (!statusFilter || row.status_ot?.includes(statusFilter)) &&
      (row.agent_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.num_ot.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.num_ot.toLowerCase().includes(searchQuery.toLowerCase())) &&
      (!agentFilter ||
        row.agent_name.toLowerCase() === agentFilter.toLowerCase())
    );
  });

  let currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  type PageItem = number | "...";

  const getPageNumbers = (): PageItem[] => {
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    const pages: PageItem[] = [];
    const visiblePages = 2;

    // Generate pages around the current page
    for (
      let i = Math.max(1, currentPage - visiblePages);
      i <= Math.min(totalPages, currentPage + 1 + visiblePages);
      i++
    ) {
      pages.push(i);
    }
    if (typeof pages[0] === "number" && pages[0] > 1) {
      pages.unshift(1);
      if (typeof pages[1] === "number" && pages[1] !== 2) {
        pages.splice(1, 0, "...");
      }
    }
    if (
      typeof pages[pages.length - 1] === "number" &&
      pages[pages.length - 1] < totalPages
    ) {
      if (
        typeof pages[pages.length - 1] === "number" &&
        pages[pages.length - 1] !== totalPages - 1
      ) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage((prevPage) => prevPage - 1);
  };

  const renderFilters = () => (
    <React.Fragment>
      <FormControl size="sm">
        <FormLabel>Status</FormLabel>
        <Select
          size="sm"
          placeholder="Filter by status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
        >
          <Option value="">All</Option>
          {uniqueStatuses.map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </FormControl>
      <FormControl size="sm">
        <FormLabel>Code Equipement</FormLabel>
        <Select
          size="sm"
          placeholder="Filter by code"
          value={equipmentCodeFilter}
          onChange={handleEquipmentCodeFilterChange}
        >
          <Option value="">All</Option>
          {uniqueCode.map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </FormControl>
      <FormControl size="sm">
        <FormLabel>Agent</FormLabel>
        <Select
          size="sm"
          placeholder="Filter by Agent"
          value={agentFilter}
          onChange={handleAgentFilterChange}
        >
          <Option value="">All</Option>
          {uniqueAgents.map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </FormControl>
    </React.Fragment>
  );
  return (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          my: 1,
          gap: 1,
        }}
      >
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          sx={{ flexGrow: 1 }}
          // onChange={handleInputChange}
        />
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <FilterAltIcon />
        </IconButton>
        {/* <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
            <ModalClose />
            <Typography id="filter-modal" level="h2">
              Filters
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Sheet sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {renderFilters()}
              <Button color="primary" onClick={() => setOpen(false)}>
                Submit
              </Button>
            </Sheet>
          </ModalDialog>
        </Modal> */}
      </Sheet>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Search for order</FormLabel>
          <Input
            size="sm"
            placeholder="Search"
            startDecorator={<SearchIcon />}
            onChange={(event: any) => {
              setSearchQuery(event.target.value);
            }}
          />
        </FormControl>
        {renderFilters()}
      </Box>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{ width: 48, textAlign: "center", padding: "12px 6px" }}
              >
                <Checkbox
                  size="sm"
                  indeterminate={
                    selected.length > 0 && selected.length !== rows.length
                  }
                  checked={selected.length === rows.length}
                  onChange={(event) => {
                    setSelected(
                      event.target.checked ? rows.map((row) => row._id) : []
                    );
                  }}
                  color={
                    selected.length > 0 || selected.length === rows.length
                      ? "primary"
                      : undefined
                  }
                  sx={{ verticalAlign: "text-bottom" }}
                />
              </th>
              <th style={{ width: 80, padding: "12px 6px" }}>
                <Link
                  underline="none"
                  color="primary"
                  component="button"
                  onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
                  fontWeight="lg"
                  endDecorator={<ArrowDropDownIcon />}
                  sx={{
                    "& svg": {
                      transition: "0.2s",
                      transform:
                        order === "desc" ? "rotate(0deg)" : "rotate(180deg)",
                    },
                  }}
                >
                  Num OT
                </Link>
              </th>
              <th style={{ width: 80, padding: "12px 6px" }}>Date</th>
              <th style={{ width: 60, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 120, padding: "12px 6px" }}>Agent</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Code</th>
              <th style={{ width: 240, padding: "12px 6px" }}>Equipement</th>
              <th style={{ width: 40, padding: "12px 6px" }}></th>
            </tr>
          </thead>
          <tbody>
            {stableSort(currentRows, getComparator(order, "_id")).map((row) => (
              <tr key={row._id}>
                <td style={{ textAlign: "center", width: 120 }}>
                  <Checkbox
                    size="sm"
                    checked={selected.includes(row._id)}
                    color={selected.includes(row._id) ? "primary" : undefined}
                    onChange={(event) => {
                      setSelected((ids) =>
                        event.target.checked
                          ? ids.concat(row._id)
                          : ids.filter((itemId) => itemId !== row._id)
                      );
                    }}
                    slotProps={{ checkbox: { sx: { textAlign: "left" } } }}
                    sx={{ verticalAlign: "text-bottom" }}
                  />
                </td>
                <td>
                  <Typography level="body-xs">{row.num_ot}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">
                    {formatDate(row.date_intervention)}
                  </Typography>
                </td>
                <td>
                  <Chip
                    variant="soft"
                    size="sm"
                    startDecorator={
                      {
                        Paid: <CheckRoundedIcon />,
                        Refunded: <AutorenewRoundedIcon />,
                        Cancelled: <BlockIcon />,
                      }[row.status_ot]
                    }
                    color={
                      {
                        Paid: "success",
                        Refunded: "neutral",
                        Cancelled: "danger",
                      }[row.status_ot] as ColorPaletteProp
                    }
                  >
                    {row.status_ot}
                  </Chip>
                </td>
                <td>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Avatar size="sm" src={row.agent_logo}>
                      {row.agent_name}
                    </Avatar>
                    <div>
                      <Typography level="body-xs">{row.agent_name}</Typography>
                      {/* <Typography level="body-xs">{row.customer.email}</Typography> */}
                    </div>
                  </Box>
                </td>
                <td>
                  <Typography level="body-xs">{row.equipment_code}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">{row.equipment_title}</Typography>
                </td>
                <td>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {/* <Link level="body-xs" component="button">
                      Download
                    </Link> */}
                    <RowMenu id={row._id} />
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
          {selectedItemId && (
            <AlertDialogModal
              item="workorder"
              id={selectedItemId}
              open={open}
              onClose={handleClose}
              onDeleteSuccess={handleDeleteSuccess}
            />
          )}
        </Table>
      </Sheet>
      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<KeyboardArrowLeftIcon />}
          onClick={handlePreviousPage}
          disabled={currentPage === 0}
        >
          Previous
        </Button>

        <Box sx={{ flex: 1 }} />
        {getPageNumbers().map((page) => (
          <IconButton
            key={typeof page === "number" ? page : "ellipsis"}
            size="sm"
            variant={currentPage + 1 === page ? "outlined" : "plain"}
            color="neutral"
            onClick={() => {
              if (typeof page === "number") setCurrentPage(page - 1);
            }}
            disabled={typeof page !== "number"}
          >
            {typeof page === "number" ? page : "..."}
          </IconButton>
        ))}
        <Box sx={{ flex: 1 }} />

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          endDecorator={<KeyboardArrowRightIcon />}
          onClick={handleNextPage}
          disabled={indexOfLastRow >= filteredRows.length}
        >
          Next
        </Button>
      </Box>
      <WorkOrderDetailsModal
        open={open}
        workOrder={selectedWorkOrder}
        onClose={handleClose}
      />
    </React.Fragment>
  );
}
